import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import { fetchDeck, swipe } from "../lib/api";
import { friendly } from "../lib/errors";
import { captureLocation, geoErrorMessage } from "../lib/geo";
import type { DeckCard } from "../lib/types";
import { scoreColor } from "../lib/bands";
import Gauge from "../components/Gauge";
import ProfileDetail from "../components/ProfileDetail";
import { CompatibilityInfoSheet, ResponsivenessInfoSheet } from "../components/InfoSheets";
import Sheet from "../components/Sheet";
import ReportSheet from "../components/ReportSheet";

type Status = "loading" | "needsLocation" | "ready" | "empty" | "error";

export default function Discover() {
  const { uid } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("loading");
  const [deck, setDeck] = useState<DeckCard[]>([]);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [matchedWith, setMatchedWith] = useState<{ name: string; matchId: string } | null>(null);
  const [reporting, setReporting] = useState(false);
  const [showCompat, setShowCompat] = useState(false);
  const [showResp, setShowResp] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const loadSeq = useRef(0);

  // Swipe gesture (iOS SwipeCard): drag with rotation, |dx| > 120 decides.
  const [dragX, setDragX] = useState(0);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const dragged = useRef(false);

  function onCardTouchStart(e: React.TouchEvent) {
    dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    dragged.current = false;
  }
  function onCardTouchMove(e: React.TouchEvent) {
    if (!dragStart.current) return;
    const dx = e.touches[0].clientX - dragStart.current.x;
    const dy = e.touches[0].clientY - dragStart.current.y;
    // Horizontal intent only — leave vertical scrolling alone.
    if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) {
      dragged.current = true;
      setDragX(dx);
    }
  }
  function onCardTouchEnd() {
    if (dragged.current && Math.abs(dragX) > 120) {
      const liked = dragX > 0;
      setDragX(liked ? 600 : -600);
      setTimeout(() => { setDragX(0); void decide(liked); }, 180);
    } else {
      setDragX(0);
    }
    dragStart.current = null;
  }

  const card = deck[0] ?? null;

  const load = useCallback(async () => {
    if (!uid) return;
    const seq = ++loadSeq.current;
    setStatus("loading");
    setError(null);
    try {
      const me = await getDoc(doc(db, "users", uid));
      if (typeof me.data()?.lat !== "number") {
        if (seq === loadSeq.current) setStatus("needsLocation");
        return;
      }
      const { deck } = await fetchDeck();
      if (seq !== loadSeq.current) return;
      setDeck(deck);
      setPhotoIdx(0);
      setStatus(deck.length ? "ready" : "empty");
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setError(friendly(e));
      setStatus("error");
    }
  }, [uid]);

  useEffect(() => {
    void load();
  }, [load]);

  async function enableLocation() {
    if (!uid) return;
    setBusy(true);
    setError(null);
    try {
      await captureLocation(uid);
      await load();
    } catch (e) {
      setError(geoErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function decide(liked: boolean) {
    if (!card || busy) return;
    setBusy(true);
    setError(null);
    const target = card;
    // Optimistic advance; restore on failure.
    setDeck((d) => d.slice(1));
    setPhotoIdx(0);
    try {
      const res = await swipe({ targetUid: target.uid, liked });
      if (liked && res.matched && res.matchId) {
        setMatchedWith({ name: target.displayName, matchId: res.matchId });
      }
      if (deck.length <= 1) await load();
    } catch (e) {
      setDeck((d) => [target, ...d]);
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  if (status === "loading") {
    return <div className="splash"><div className="spinner" /></div>;
  }

  if (status === "needsLocation") {
    return (
      <div className="screen center" style={{ paddingTop: 60 }}>
        <h1>Find people nearby</h1>
        <p className="muted">
          Discover shows people within your chosen radius. Fuse stores your approximate
          location (about 1&nbsp;km) — never your exact position.
        </p>
        <button className="btn" disabled={busy} onClick={enableLocation}>
          {busy ? "Locating…" : "Enable location"}
        </button>
        {error && <div className="error">{error}</div>}
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="screen center" style={{ paddingTop: 60 }}>
        <h1>Discover</h1>
        <div className="error">{error}</div>
        <button className="btn" onClick={() => void load()}>Retry</button>
      </div>
    );
  }

  if (status === "empty" || !card) {
    // Empty state matching the iOS screen: title top-left, sparkles + copy +
    // compact pink pill anchored just above the tab bar. Pull down to refresh.
    return (
      <div className="screen deck-screen">
        <h1 style={{ marginTop: 20 }}>Discover</h1>
        <div style={{ flex: 1 }} />
        <div className="center">
          <div style={{ fontSize: 46, marginBottom: 10 }} aria-hidden>✨</div>
          <h2 style={{ fontSize: 24, margin: "0 0 8px" }}>That's everyone for now</h2>
          <p className="muted" style={{ margin: "0 0 18px" }}>
            You've seen everyone nearby. Jump into a Relay to meet new people live — or
            widen your search radius in Profile.
          </p>
          <button className="btn btn-inline" onClick={() => navigate("/relay")}>
            <svg width="16" height="16" viewBox="0 0 100 100" aria-hidden>
              <g fill="currentColor">
                <circle cx="28" cy="30" r="13" /><circle cx="72" cy="30" r="13" />
                <circle cx="50" cy="72" r="13" /><circle cx="50" cy="27" r="4.5" />
                <circle cx="37" cy="48" r="4.5" /><circle cx="63" cy="48" r="4.5" />
              </g>
            </svg>
            Jump into a Relay
          </button>
        </div>
        <div style={{ flex: 1 }} />
      </div>
    );
  }

  const photos = card.photoURLs.length ? card.photoURLs : [null];
  return (
    <div className="screen deck-screen">
      <h1 style={{ marginTop: 20 }}>Discover</h1>
      <div
        className="deck-card"
        style={{
          transform: dragX ? `translateX(${dragX}px) rotate(${dragX / 25}deg)` : undefined,
          transition: dragStart.current ? "none" : "transform 0.18s ease-out",
          touchAction: "pan-y",
        }}
        onTouchStart={onCardTouchStart}
        onTouchMove={onCardTouchMove}
        onTouchEnd={onCardTouchEnd}
        onTouchCancel={onCardTouchEnd}
      >
        <div
          className="deck-photo"
          onClick={(e) => {
            const { left, width } = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const back = e.clientX - left < width / 2;
            setPhotoIdx((i) => Math.min(Math.max(0, back ? i - 1 : i + 1), photos.length - 1));
          }}
        >
          {photos[photoIdx] ? (
            <img src={photos[photoIdx]!} alt={`${card.displayName}'s photo`} draggable={false} />
          ) : (
            <div className="deck-photo-empty">☺</div>
          )}
          {photos.length > 1 && (
            <div className="photo-dots">
              {photos.map((_, i) => (
                <span key={i} className={"dot" + (i === photoIdx ? " dot-on" : "")} />
              ))}
            </div>
          )}

          {/* Responsiveness gauge, top-left (tap → explainer) — from SwipeCard. */}
          {card.responsiveness !== null && (
            <button
              className="card-gauge"
              aria-label={`Responsiveness ${card.responsiveness} of 100. Tap for details.`}
              onClick={(e) => { e.stopPropagation(); setShowResp(true); }}
            >
              <Gauge value={card.responsiveness} size={44} color={scoreColor(card.responsiveness)} />
            </button>
          )}
          {/* Compatibility badge, top-right (tap → explainer). */}
          {card.compatibility !== null && (
            <button
              className="card-compat"
              style={{ color: scoreColor(card.compatibility) }}
              aria-label={`${card.compatibility} percent compatible. Tap for details.`}
              onClick={(e) => { e.stopPropagation(); setShowCompat(true); }}
            >
              {card.compatibility}% ⓘ
            </button>
          )}

          <div className="deck-grad" />
          <div className="deck-title" onClick={(e) => { e.stopPropagation(); setShowDetail(true); }}>
            <span>
              <span className="deck-name">{card.displayName}, {card.age}</span>
              {card.boosted && <span className="boost-pill" style={{ position: "static", display: "inline-block", marginLeft: 8 }}>◉ From your Relay</span>}
              {card.headline && <span className="deck-headline-overlay">{card.headline}</span>}
              <span className="deck-dist" style={{ display: "block" }}>
                📍 {Number.isFinite(card.distanceMiles) ? `${card.distanceMiles} miles away` : ""}
              </span>
            </span>
            <button
              className="deck-expand"
              aria-label="Show full profile"
              onClick={(e) => { e.stopPropagation(); setShowDetail(true); }}
            >⌃</button>
          </div>
        </div>

        {/* Like SwiftUI's deck: only a 2-line bio under the photo — prompts and
            the full story live in the tap-through profile view. */}
        <div className="deck-body deck-body-compact">
          {card.bio && <p className="deck-bio deck-bio-clamp">{card.bio}</p>}
        </div>
      </div>

      <div className="decide-row" style={{ flexShrink: 0 }}>
        <button className="decide decide-pass" aria-label="Pass" disabled={busy} onClick={() => void decide(false)}>✕</button>
        <button
          className="btn-ghost-sm"
          style={{ margin: 0, alignSelf: "center" }}
          onClick={() => setReporting(true)}
        >⚑</button>
        <button className="decide decide-like" aria-label="Like" disabled={busy} onClick={() => void decide(true)}>♥</button>
      </div>
      {error && <div className="error center">{error}</div>}

      <Sheet open={!!matchedWith} onClose={() => setMatchedWith(null)}>
        <div className="center">
          <div style={{ fontSize: 44 }}>⚡</div>
          <h2 style={{ margin: "8px 0 4px" }}>It's a match!</h2>
          <p className="muted">You and {matchedWith?.name} each get 10 messages. Make them count.</p>
          <button
            className="btn"
            onClick={() => {
              const id = matchedWith?.matchId;
              setMatchedWith(null);
              if (id) navigate(`/chat/${id}`);
            }}
          >
            Say hi
          </button>
          <button className="btn btn-ghost" onClick={() => setMatchedWith(null)}>Keep browsing</button>
        </div>
      </Sheet>

      <ReportSheet
        targetUid={card.uid}
        targetName={card.displayName}
        open={reporting}
        onClose={() => setReporting(false)}
        onDone={() => {
          setReporting(false);
          setDeck((d) => d.slice(1));
          setPhotoIdx(0);
        }}
      />

      {card.compatibility !== null && (
        <CompatibilityInfoSheet
          pct={card.compatibility}
          name={card.displayName}
          open={showCompat}
          onClose={() => setShowCompat(false)}
        />
      )}
      {card.responsiveness !== null && (
        <ResponsivenessInfoSheet
          pct={card.responsiveness}
          name={card.displayName}
          open={showResp}
          onClose={() => setShowResp(false)}
        />
      )}
      {showDetail && (
        <ProfileDetail
          profile={card}
          onClose={() => setShowDetail(false)}
          onLike={() => void decide(true)}
          onPass={() => void decide(false)}
        />
      )}
    </div>
  );
}
